import { defineStore } from "pinia";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { useProjectsStore } from "@/store/modules/projects";
import { useInstallationsStore } from "@/store/modules/installations";

const { innerFetch, innerCreate, innerUpdate, innerDelete } = crudGenerator(
  db.models.Sum
);

export const useSumStore = defineStore("sum", {
  state: () => ({
    sumList: [],
    totalSum: 0,
    sumIsLoading: false,
    currentSum: {}, // Object  { param1Id: param1, param2Id: param2 ....}
    currentSumName: "",
  }),
  actions: {
    async fetchSums(projectUuid) {
      return innerFetch(
        { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.sumIsLoading = isLoading;
        }
      );
    },

    async setSums(installationUuid) {
      if (!installationUuid) return;
      while (this.sumIsLoading) await new Promise((r) => setTimeout(r, 200));
      this.sumIsLoading = true;
      try {
        const sumList = await db.sums
          .where("installation")
          .equals(installationUuid)
          .sortBy("createdAt");
        for (const sum of sumList) {
          await sum.setRelationships();
        }
        this.sumList = sumList;
        this.totalSums = sumList.length;
      } finally {
        this.sumIsLoading = false;
      }
    },

    async createSum(form) {
      const sum = await innerCreate(form, useProjectsStore().isOfflineState);
      await useInstallationsStore().refreshLastUpdated();
      this.sumList.push(sum);
      this.totalSums += 1;
      return sum;
    },

    async updateSum(form) {
      const sum = await innerUpdate(form, useProjectsStore().isOfflineState);
      await useInstallationsStore().refreshLastUpdated();
      const index = this.sumList.findIndex(({ uuid }) => sum.uuid === uuid);
      if (index >= 0) this.sumList[index] = sum;
      return sum;
    },

    async deleteSum(sum) {
      const projectsStore = useProjectsStore();
      await innerDelete(
        sum.uuid,
        projectsStore.isOfflineState,
        projectsStore.currentProjectUuid
      );
      await useInstallationsStore().refreshLastUpdated();

      this.sumList = this.sumList.filter(({ uuid }) => uuid !== sum.uuid);
    },

    updateCurrentSum(currentSum) {
      this.currentSum = currentSum;
    },

    updateCurrentSumName(currentSumName) {
      this.currentSumName = currentSumName;
    },

    addParameterToCurrentSum(parameter) {
      this.currentSum = { ...this.currentSum, [parameter.uuid]: parameter };
    },

    deleteParameterFromCurrentSum(parameter) {
      const currentSum = { ...this.currentSum };
      delete currentSum[parameter.uuid];
      this.currentSum = currentSum;
    },
  },
});
